/**
 * NOLAS 2026, © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/api/impersonation.ts
 *
 * Rôle :
 *   Client typé de `api/admin/impersonate.php`, « Voir en tant que ».
 *   Permet à un super-admin de consulter l'application avec les droits
 *   RÉELS d'un autre compte, pour vérifier ce qu'il voit vraiment plutôt
 *   que de le déduire d'une table de permissions.
 *
 *   Le contrat est validé par Zod À L'EXÉCUTION : mieux vaut une erreur
 *   nette qu'un bandeau qui affiche « undefined » alors qu'une usurpation
 *   est en cours, ici, une information fausse serait dangereuse.
 *
 * Garde-fous (imposés par le SERVEUR, jamais par cette couche) :
 *   - super_admin uniquement, jamais un autre super_admin pour cible ;
 *   - LECTURE SEULE : toute écriture est refusée pendant l'usurpation ;
 *   - expiration automatique au bout d'une heure ;
 *   - l'acteur réel reste tracé dans le journal d'audit.
 *
 * Réutilisable :
 *   100 %. Aucun champ métier NOLAS, aucun rôle en dur.
 *
 * Voir aussi : app/public_html/api/_lib/impersonation.php (les garde-fous).
 *
 * @author Mikael
 * @since  2026-08-25
 */

import { z } from 'zod';
import { apiRequest, apiMutate, ApiError } from './client';

/** Compte actuellement usurpé. `label` est déjà prêt à afficher. */
export const ImpersonationTargetSchema = z.object({
  id: z.number(),
  role: z.string(),
  label: z.string(),
});
export type ImpersonationTarget = z.infer<typeof ImpersonationTargetSchema>;

/**
 * État renvoyé par les trois actions (`status`, `start`, `stop`).
 * `expires_in_seconds` est `null` hors usurpation.
 */
export const ImpersonationStateSchema = z.object({
  ok: z.literal(true),
  impersonating: z.boolean(),
  target: ImpersonationTargetSchema.nullable().optional().default(null),
  expires_in_seconds: z.number().nullable().optional().default(null),
  read_only: z.boolean().optional().default(true),
});
export type ImpersonationState = z.infer<typeof ImpersonationStateSchema>;

/**
 * Transforme une réponse en état exploitable SANS jamais échouer.
 *
 * Réservé aux actions (start/stop) dont l'effet est déjà produit côté
 * serveur : le HTTP 200 fait foi. `fetchImpersonationStatus`, lui, reste
 * strict, un bandeau qui afficherait une identité fausse serait pire que
 * pas de bandeau du tout.
 */
function coerceState(
  raw: unknown,
  fallback: { impersonating: boolean },
): ImpersonationState {
  const parsed = ImpersonationStateSchema.safeParse(raw);
  if (parsed.success) return parsed.data;
  // Non bloquant, mais tracé : une forme inattendue reste un signal.
  console.warn('[impersonation] réponse inattendue du serveur', parsed.error);
  return {
    ok: true,
    impersonating: fallback.impersonating,
    target: null,
    expires_in_seconds: null,
    read_only: true,
  };
}

/**
 * Rôles de BACK-OFFICE, miroir de `IMPERSONATION_ADMIN_ROLES` côté PHP.
 * Sert uniquement à choisir la page d'atterrissage : le serveur reste seul
 * juge de ce qui est réellement accessible.
 */
const ROLES_BACKOFFICE = [
  'super_admin',
  'admin',
  'editeur',
  'auteur',
  'moderateur',
  'staff',
  'vip_manager',
] as const;

/**
 * Où atterrir après avoir emprunté une identité.
 *
 * FIX 2026-08-26, on renvoyait TOUJOURS vers `/admin/dashboard`, y compris
 * en empruntant un exposant : on voyait donc le back-office sous un bandeau
 * annonçant qu'on voyait l'espace de l'exposant. Un participant n'a pas
 * d'administration : sa vraie vue, c'est SON espace.
 */
export function impersonationLandingPath(role: string | null | undefined): string {
  if (role && (ROLES_BACKOFFICE as readonly string[]).includes(role)) {
    return '/admin/dashboard';
  }
  // FIX 2026-08-26 (2e passe, signale par Mika), un EXPOSANT n'a rien a
  // faire dans `/mon-espace`, qui est l'espace VISITEUR. Sa fiche est le
  // premier ecran utile : c'est celui qu'on veut voir et corriger quand on
  // accompagne quelqu'un au telephone.
  if (role === 'exhibitor') {
    return '/exposant/ma-fiche';
  }
  return '/mon-espace';
}

/** Clé react-query partagée : le bandeau et les boutons lisent le même état. */
export const IMPERSONATION_QUERY_KEY = ['me', 'impersonation'] as const;

/** État courant. Appelé par le bandeau, à chaque écran admin. */
export async function fetchImpersonationStatus(): Promise<ImpersonationState> {
  const raw = await apiRequest<unknown>('admin/impersonate.php?action=status');
  return ImpersonationStateSchema.parse(raw);
}

/**
 * Extrait le message EXPLICITE du serveur s'il y en a un.
 *
 * FIX 2026-08-26, la première version noyait toutes les raisons de refus
 * dans un seul message générique (« pas usurpable, un super-administrateur
 * peut-être »). Or le serveur en distingue cinq, toutes actionnables :
 * usurpation déjà en cours, son propre compte, un autre super-administrateur,
 * un compte inactif, un jeton CSRF périmé. Afficher « peut-être » quand le
 * serveur dit précisément quoi, c'est fabriquer une énigme.
 */
export function impersonationErrorMessage(err: unknown): string | null {
  if (err instanceof ApiError) {
    const payload = err.payload as { message?: unknown; error?: unknown } | null;
    if (payload && typeof payload.message === 'string' && payload.message !== '') {
      return payload.message;
    }
    if (payload && typeof payload.error === 'string' && payload.error !== '') {
      return payload.error;
    }
  }
  return null;
}

/** Démarre l'usurpation d'un compte. Le serveur refuse toute cible illégitime. */
export async function startImpersonation(userId: number): Promise<ImpersonationState> {
  const res = await apiMutate<unknown>(
    'admin/impersonate.php?action=start',
    'POST',
    { user_id: userId },
    { kind: 'admin.impersonate.start', offline: false },
  );
  if (res.kind !== 'ok') {
    throw new Error('impersonation_start_not_confirmed');
  }
  // FIX 2026-08-26 (bug prod : « erreur affichée, mais après actualisation
  // l'usurpation fonctionne »). Le serveur avait RÉUSSI (audit à l'appui) ;
  // c'est la validation d'affichage qui rejetait sa réponse, et mon code en
  // faisait un échec, alors que l'effet de bord était déjà produit.
  //
  // Règle : une validation de FORME ne doit jamais annuler une action déjà
  // accomplie côté serveur. Le HTTP 200 fait foi ; si la forme surprend, on
  // se rabat sur un état minimal cohérent et on le signale en console.
  return coerceState(res.data, { impersonating: true });
}

/** Revient à son propre compte. */
export async function stopImpersonation(): Promise<ImpersonationState> {
  const res = await apiMutate<unknown>(
    'admin/impersonate.php?action=stop',
    'POST',
    {},
    { kind: 'admin.impersonate.stop', offline: false },
  );
  if (res.kind !== 'ok') {
    throw new Error('impersonation_stop_not_confirmed');
  }
  return coerceState(res.data, { impersonating: false });
}
